/**
 * 用户快捷键覆盖层（M5-03 / DESIGN.md §7.4）。
 *
 * 叠在 `registry.ts` 每个 `ActionDefinition.shortcut`（§7.4 表格照抄
 * Photoshop 默认值）**之上**，不直接改注册表——注册表是"这个应用出厂时
 * 什么样"，覆盖层是"这个用户改成了什么样"，两者分开才谈得上"恢复默认"。
 *
 * 覆盖值三态：
 * - 没有这个 key：没改过，用默认值。
 * - `Shortcut`：改绑成了这个组合键。
 * - `null`：用户显式清空了快捷键（"这个动作我不要有快捷键"），
 *   跟"没改过"不是一回事——不能把 `null` 当成"没有覆盖"处理，否则
 *   "清空"这个操作本身没法持久化，一刷新又变回默认值。
 */
import type { ActionDefinition, Shortcut } from './registry';
import { matchesShortcut, shortcutsEqual, type KeyEventLike } from './keymap';
import { openKvStore, type KvStore } from '../../io/kv-store';

export type ShortcutOverrideValue = Shortcut | null;
export type ShortcutOverrides = ReadonlyMap<string, ShortcutOverrideValue>;

const DB_NAME = 'photosmith-shortcut-overrides';

export class ShortcutOverrideStore {
  #overrides: ShortcutOverrides = new Map();
  #store: KvStore | null = null;
  readonly #listeners = new Set<() => void>();
  /** 首次从持久化存储加载完成的信号——测试与"编辑器打开时数据还没到"两处都要等它。 */
  readonly ready: Promise<void>;

  constructor(store?: KvStore) {
    this.ready = this.#init(store);
  }

  async #init(store?: KvStore): Promise<void> {
    this.#store = store ?? (await openKvStore(DB_NAME));
    const entries = await this.#store.entries<ShortcutOverrideValue>();
    if (entries.length > 0) {
      this.#overrides = new Map(entries);
      this.#notify();
    }
  }

  get overrides(): ShortcutOverrides {
    return this.#overrides;
  }

  subscribe(fn: () => void): () => void {
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  }

  #notify(): void {
    for (const fn of this.#listeners) fn();
  }

  /** 改绑一个动作的快捷键；`shortcut` 传 `null` = 显式清空（不是恢复默认，见文件头）。 */
  set(actionId: string, shortcut: ShortcutOverrideValue): void {
    const next = new Map(this.#overrides);
    next.set(actionId, shortcut);
    this.#overrides = next;
    this.#notify();
    void this.#store?.set(actionId, shortcut);
  }

  /** 恢复单条默认——删掉覆盖记录，而不是设成 `undefined`（三态里没有这一档）。 */
  resetOne(actionId: string): void {
    if (!this.#overrides.has(actionId)) return;
    const next = new Map(this.#overrides);
    next.delete(actionId);
    this.#overrides = next;
    this.#notify();
    void this.#store?.delete(actionId);
  }

  /** 恢复全部默认。 */
  resetAll(): void {
    if (this.#overrides.size === 0) return;
    const ids = [...this.#overrides.keys()];
    this.#overrides = new Map();
    this.#notify();
    void Promise.all(ids.map((id) => this.#store?.delete(id)));
  }
}

/** 覆盖后的有效快捷键——`override` 为 `undefined`（没改过）用默认值，
 * `null`（显式清空）视为没有快捷键，否则用覆盖值。 */
export function effectiveShortcut(
  action: ActionDefinition,
  overrides: ShortcutOverrides,
): Shortcut | undefined {
  if (!overrides.has(action.id)) return action.shortcut;
  return overrides.get(action.id) ?? undefined;
}

/**
 * 把覆盖层套到一份动作列表上，产出"每个动作的 `shortcut` 都已经是有效值"
 * 的新列表——`buildMenuModel`/`paletteEntries`/键盘事件匹配三处消费方
 * 都读 `action.shortcut`，套在这一层就不用把 `overrides` 这个参数
 * 到处传染下去。
 */
export function withEffectiveShortcuts(
  actions: readonly ActionDefinition[],
  overrides: ShortcutOverrides,
): readonly ActionDefinition[] {
  if (overrides.size === 0) return actions;
  return actions.map((action) => {
    if (!overrides.has(action.id)) return action;
    const shortcut = effectiveShortcut(action, overrides);
    if (shortcut !== undefined) return { ...action, shortcut };
    // 显式清空：drop `shortcut` 键，不是设成 `undefined`（exactOptionalPropertyTypes
    // 下二者不等价——`shortcut?: Shortcut` 不接受显式 `undefined` 赋值）
    const rest: ActionDefinition = { ...action };
    delete (rest as { shortcut?: Shortcut }).shortcut;
    return rest;
  });
}

/**
 * 某个候选快捷键是否已经被**另一个**动作占用（套过覆盖层之后的有效值）。
 * 返回占用者；没有冲突返回 `null`。
 */
export function findShortcutConflict(
  actions: readonly ActionDefinition[],
  overrides: ShortcutOverrides,
  excludeActionId: string,
  candidate: Shortcut,
): ActionDefinition | null {
  for (const action of actions) {
    if (action.id === excludeActionId) continue;
    const shortcut = effectiveShortcut(action, overrides);
    if (shortcut !== undefined && shortcutsEqual(shortcut, candidate)) return action;
  }
  return null;
}

/**
 * `ActionRegistry.actionForKey` 的覆盖层感知版本——键盘事件 → 动作，
 * 但拿有效快捷键（覆盖优先）去匹配，不是注册表里的出厂默认值。
 * `ActionRegistry` 自身不认识覆盖层（分层/职责边界：注册表是"出厂什么样"，
 * 覆盖层是应用层再叠一层用户偏好），所以是独立函数而不是往
 * `ActionRegistry` 类上加方法。
 */
export function actionForKeyWithOverrides(
  actions: readonly ActionDefinition[],
  overrides: ShortcutOverrides,
  event: KeyEventLike,
): ActionDefinition | null {
  for (const action of actions) {
    const shortcut = effectiveShortcut(action, overrides);
    if (shortcut !== undefined && matchesShortcut(shortcut, event)) return action;
  }
  return null;
}
